// File: frontend/src/constants/settings.constants.ts
export const MATCHES_PER_PAGE = 10;
export const ENTRIES_PER_PAGE = 10;
export const TRASH_PAGE_SIZE = 20;
// Minimum number of trash entries required to show bulk-action UI (checkboxes, select-all, empty-all)
export const TRASH_BULK_MIN = 2;
export const MODLOG_PAGE_SIZE = 20;

// Drafts page — mirrors the trash-page pattern
export const DRAFTS_PAGE_SIZE = 20;
// Minimum number of drafts required to show bulk-action UI (checkboxes, select-all)
export const DRAFTS_BULK_MIN = 2;

// How often (in ms) to poll for new entries in the selected match
export const NEW_ENTRY_POLL_INTERVAL_MS = 30000;
export const MAX_ENTRY_CONTENT_LENGTH = 4000;
export const FALLBACK_USERNAME_FOR_ENTRY_POST ="Anonymous";
export const FALLBACK_USERID_FOR_ENTRY_POST = 1;

// Session timeout settings (in minutes)
export const SESSION_INACTIVITY_TIMEOUT_MINUTES = 30; // Logout after 30 minutes of inactivity
export const SESSION_MAX_AGE_HOURS = 24; // Force logout after 24 hours regardless of activity

// Toast notification durations (in milliseconds)
export const TOAST_DURATION_SHORT = 2500; // Copy confirmation toasts
export const TOAST_DURATION_NORMAL = 4000; // Rate limit warnings
export const TOAST_DURATION_LONG = 5000; // Important messages

// Redirect delays (in milliseconds)
export const REDIRECT_DELAY_MS = 1500;

// Account deletion — must match SettingConstants.java on backend
export const ACCOUNT_DELETION_GRACE_DAYS = 14;
export const ACCOUNT_DELETION_CANCEL_COOLDOWN_HOURS = 24; // Hours after requesting during which cancellation is locked

// Account freeze — must match SettingConstants.java on backend
export const ACCOUNT_FREEZE_CANCEL_COOLDOWN_HOURS = 24; // Hours after requesting freeze during which cancel-in-settings is locked

// Bio / personal message — must match SettingConstants.java on backend
export const BIO_MAX_LENGTH = 100;

// Password rules — must match SettingConstants.java on backend.
// 8..64 is the common market range (NIST SP 800-63B: minimum 8, accept at least 64).
// Used by RegisterModal, ResetPasswordPage, SettingsPage and AdminUsers so every
// password field in the app states and enforces the same rule.
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 64;

// Admin panel UI timing (in milliseconds)
export const ADMIN_SUCCESS_DISMISS_MS = 3000; // Auto-dismiss success banners
export const ADMIN_PAGE_SIZE = 20; // Default page size for admin tables
export const DROPDOWN_BLUR_DELAY_MS = 200; // Delay before closing dropdown on blur (allows click to register)

// Admin table content preview
export const ADMIN_CONTENT_PREVIEW_MAX_LENGTH = 80;

// Suspension defaults
export const SUSPENSION_DEFAULT_DAYS = 7;
export const SUSPENSION_MAX_DAYS = 365;

// Year range for date inputs (person birth year, stadium built year, etc.)
export const YEAR_MIN = 1800;
export const YEAR_MAX = 2100;

// Search / debounce settings
export const SEARCH_DEBOUNCE_MS = 300; // Debounce delay for search inputs
export const SEARCH_MAX_RESULTS = 8; // Max results for ref/match search popups
export const SEARCH_MIN_CHARS = 3; // Minimum characters before typeahead fires — must match SettingConstants.java
// Entry report settings — must match SettingConstants.java on backend
export const REPORT_OTHER_REASON_MAX_LENGTH = 500; // Max chars for free-text "other" reason
export const REPORT_RESOLUTION_NOTE_MAX_LENGTH = 500; // Max chars for admin resolution note

// ==========================================================================
// Admin panel field length limits.
//
// Every value below mirrors the length of the DB column the field is written
// into (see the matching @Column(length = ...) in backend/domain). They drive
// both the maxLength attribute and the <AdminCharCounter /> shown under the
// field, so an admin can never type past what the column accepts.
//
// When a column length changes, change it here too — nothing else reads these.
// ==========================================================================

// Shared free-text limits
export const ADMIN_REASON_MAX_LENGTH = 500;        // lock / ban / delete / edit reason columns
export const ADMIN_ADMIN_NOTE_MAX_LENGTH = 255;    // custom_topic_suggestions.admin_note
export const ADMIN_URL_MAX_LENGTH = 500;           // logo_url / photo_url / image_url columns
export const ADMIN_EMAIL_MAX_LENGTH = 255;         // users.email

// Banned IP
export const ADMIN_IP_ADDRESS_MAX_LENGTH = 45;     // banned_ips.ip_address (IPv6-sized)

// Club
export const ADMIN_CLUB_SHORT_NAME_MAX_LENGTH = 100;
export const ADMIN_CLUB_NAME_MAX_LENGTH = 100;     // name_tr / name_en
export const ADMIN_CLUB_LONG_NAME_MAX_LENGTH = 255;
export const ADMIN_CLUB_ABBREVIATION_MAX_LENGTH = 10;
export const ADMIN_CITY_MAX_LENGTH = 255;
export const ADMIN_CLUB_COLOR_MAX_LENGTH = 7;    // clubs.color1..3 — "#RRGGBB"

// Team
export const ADMIN_TEAM_CODE_MAX_LENGTH = 20;
export const ADMIN_TEAM_DISPLAY_NAME_MAX_LENGTH = 200;

// Stadium
export const ADMIN_STADIUM_NAME_MAX_LENGTH = 255;
export const ADMIN_STADIUM_SHORT_NAME_MAX_LENGTH = 100;
export const ADMIN_STADIUM_NICKNAME_MAX_LENGTH = 200;
export const ADMIN_STADIUM_ADDRESS_MAX_LENGTH = 500;

// Person
export const ADMIN_PERSON_NAME_MAX_LENGTH = 100;   // first_name / last_name / known_name
export const ADMIN_PERSON_BIRTH_CITY_MAX_LENGTH = 100;
export const ADMIN_PERSON_BIO_MAX_LENGTH = 1000;
export const ADMIN_CAREER_NOTES_MAX_LENGTH = 500;
export const ADMIN_CAREER_CLUB_NAME_MAX_LENGTH = 200; // person_career_entries.custom_club_name

// Match
export const ADMIN_MATCH_LEAGUE_MAX_LENGTH = 255;
export const ADMIN_MATCH_ROUND_MAX_LENGTH = 100;
export const ADMIN_MATCH_STADIUM_NAME_MAX_LENGTH = 255;

// Custom topic (gündem)
export const ADMIN_TOPIC_TITLE_MAX_LENGTH = 80;   // custom_topics.title
export const ADMIN_TOPIC_SLUG_MAX_LENGTH = 255;   // custom_topics.slug

// Master data (MD tab)
export const ADMIN_MD_CODE_MAX_LENGTH = 10;        // md_countries.code / md_leagues.country_code
export const ADMIN_MD_FLAG_MAX_LENGTH = 20;        // flag emoji columns
export const ADMIN_MD_NAME_MAX_LENGTH = 255;       // md_leagues.name / md_rounds.name
export const ADMIN_MD_COUNTRY_NAME_MAX_LENGTH = 100;
export const ADMIN_MD_ROLE_NAME_MAX_LENGTH = 100;  // md_person_roles.name_tr / name_en
export const ADMIN_MD_CATEGORY_NAME_MAX_LENGTH = 50; // md_topic_categories.name
export const ADMIN_MD_ROLE_CODE_MAX_LENGTH = 50;     // md_person_roles.code
export const ADMIN_MD_ROLE_CATEGORY_MAX_LENGTH = 50; // md_person_roles.category
export const ADMIN_MD_ICON_KEY_MAX_LENGTH = 80;      // md_*.icon_key

// ========== Messaging settings — must match SettingConstants.java on backend ==========
// Fallback mirror of the messaging.max_length seed value. The composer reads the
// live value from useSiteSettings().messageMaxLength — this constant is only the
// number to fall back on before that request lands.
export const MESSAGE_CONTENT_MAX_LENGTH = 2000; // Max chars per message
export const MESSAGES_PER_PAGE = 30; // Messages loaded per page in conversation view
export const CONVERSATIONS_PER_PAGE = 20; // Conversations loaded per page in list